import { Decimal } from "classes/DecimalClasses";

import { i18n } from "services/i18nService";
import {
  extractName,
  formatDecimal,
  formatDocumentInfo,
  formatDateTimeToDate,
  formatNumberLinkField,
  extractLinkedTransactions,
  extractItemNamesFromReferenceArray,
  truncate,
  formatBooleanToYesOrBlank,
  formatQuantityWithUom,
  extractDescription,
  getQuantityTotal,
  formatLinkableItem,
} from "services/utility/formatting";
import { formatLineDecimal } from "services/utility/list";
import { parseHTML } from "services/utility/parsing";
import { starColumn, TYPES } from "services/utility/schema";

import { NO_REF_NUMBER_STRING } from "appConstants";
import { DEFAULT_DECIMALS_UNROUNDED } from "appConstants";

export const columnInfo = [
  starColumn,
  {
    name: "number",
    fixed: true,
    heading: i18n("columns.ReferenceNumber"),
    formatFunc: formatNumberLinkField,
    minWidth: "7em",
    linkField: true,
    sortable: true,
  },
  {
    name: "date",
    heading: i18n("columns.Date"),
    formatFunc: formatDateTimeToDate,
    minWidth: "8em",
    defaultSort: true,
    sortDirection: "asc",
    sortable: true,
    default: true,
  },
  {
    name: "id",
    heading: i18n("columns.System"),
    sortDirection: "asc",
    minWidth: "7em",
    align: "right",
    sortable: true,
    default: true,
  },
  {
    name: "outputs",
    heading: i18n("columns.Assembly"),
    formatFunc: extractItemNamesFromReferenceArray,
    minWidth: "15em",
    sortable: true,
    default: true,
  },
  {
    name: "location",
    heading: i18n("columns.Location"),
    formatFunc: extractName,
    sortable: true,
    default: true,
  },
  {
    name: "quantity",
    fieldName: "total",
    heading: i18n("columns.Qty"),
    formatFunc: formatDecimal,
    align: "right",
    sortable: true,
    default: true,
  },
  {
    name: "comment",
    heading: i18n("columns.Comment"),
    formatFunc: [parseHTML, truncate],
    downloadFn: parseHTML,
    minWidth: "25em",
    maxWidth: "25em",
    sortable: true,
    default: true,
  },
  {
    name: "documents",
    heading: i18n("columns.Documents"),
    formatFunc: formatDocumentInfo,
    minWidth: "15em",
    default: true,
  },
  {
    name: "workcenter",
    heading: i18n("columns.WorkCenter"),
    formatFunc: extractName,
    minWidth: "10em",
    sortable: true,
  },
  {
    name: "linkedTransaction",
    heading: i18n("columns.WorkOrder"),
    fieldName: "linkedWorkOrder",
    formatFunc: extractLinkedTransactions,
    minWidth: "10em",
    sortable: true,
  },
  {
    name: "department",
    heading: i18n("columns.Department"),
    formatFunc: extractName,
    sortable: true,
  },
  {
    name: "hasSignature",
    heading: i18n("columns.Signature"),
    formatFunc: formatBooleanToYesOrBlank,
  },
];

export function getEmptyRecord(settings) {
  return {
    archived: false,
    autoSerialLotNumbering: settings.autoSerialLotNumbers,
    comment: null,
    customFields: [],
    date: new Date(),
    department: null,
    documents: [],
    hasSignature: false,
    inputs: [],
    job: null,
    keys: null,
    linkedTransaction: null,
    linkedWorkOrder: null,
    location: null,
    number: "",
    outputs: [],
    syncToken: 0,
    starred: 0,
    total: Decimal.ZERO,
    summaryOnly: false,
    values: null,
    workcenter: null,
  };
}

export const OUTPUTS_METADATA = {
  lineNumber: {},
  item: {
    type: TYPES.reference,
    initialValue: { id: null, name: null, description: null },
  },
  class: { type: TYPES.reference },
  jobWorkcenter: { type: TYPES.reference },
  tax: {
    type: TYPES.decimal,
    editStr: formatLineDecimal,
  },
  linkedTransaction: { type: TYPES.linkedTransaction },
  linkedTransactionRefNumber: { type: TYPES.text },
  linkedTransactionLineNumber: { type: TYPES.number },
  description: { type: TYPES.text },
  notes: { type: TYPES.text },
  quantity: {
    type: TYPES.decimal,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    initialValue: Decimal.ZERO,
  },
  weight: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  weightunit: { type: TYPES.text },
  volume: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  volumeunit: { type: TYPES.text },
  uom: { type: TYPES.reference },
  bin: { type: TYPES.reference },
  lot: { type: TYPES.reference },
  lotExpiration: { type: TYPES.date },
  serials: { type: TYPES.arrayOfReferences, initialValue: null },
  onhand: {
    type: TYPES.itemField,
    editStr: formatLineDecimal,
    initialValue: Decimal.ZERO,
  },
  onSO: {
    type: TYPES.itemField,
    editStr: formatLineDecimal,
    initialValue: Decimal.ZERO,
  },
  itemDetails: { initialValue: {} },
  relatedRecords: { initialValue: { item: {} } },
};
export const INPUTS_METADATA = {
  lineNumber: {},
  item: {
    type: TYPES.reference,
    initialValue: { id: null, name: null, description: null },
  },
  class: { type: TYPES.reference },
  jobWorkcenter: { type: TYPES.reference },
  tax: {
    type: TYPES.decimal,
    editStr: formatLineDecimal,
  },
  linkedTransaction: { type: TYPES.linkedTransaction },
  linkedTransactionRefNumber: { type: TYPES.text },
  linkedTransactionLineNumber: { type: TYPES.number },
  description: { type: TYPES.text },
  notes: { type: TYPES.text },
  quantity: {
    type: TYPES.decimal,
    placesRight: DEFAULT_DECIMALS_UNROUNDED,
    initialValue: Decimal.ZERO,
  },
  weight: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  weightunit: {
    type: TYPES.text,
  },
  volume: { type: TYPES.decimal, initialValue: Decimal.ZERO },
  volumeunit: {
    type: TYPES.text,
  },
  worker: { type: TYPES.reference },
  uom: { type: TYPES.reference },
  bin: { type: TYPES.reference },
  lot: { type: TYPES.reference },
  lotExpiration: { type: TYPES.date },
  serials: { type: TYPES.arrayOfReferences, initialValue: null },
  onhand: {
    type: TYPES.itemField,
    editStr: formatLineDecimal,
    initialValue: Decimal.ZERO,
  },
  itemDetails: { initialValue: {} },
  relatedRecords: {
    initialValue: { item: {} },
  },
};

export const LINE_ITEM_METADATA = {
  //TODO: Update LineComponents to handle Input/Output editStr
  quantity: { placesRight: DEFAULT_DECIMALS_UNROUNDED },
  linkedTransaction: { transactionType: "WO" },
};

export const FIELDS_TO_TRANSFORM = {
  DECIMAL_FIELDS: [["total"]],
  MONEY_FIELDS: [],
  DATE_FIELDS: [],
  DATETIME_FIELDS: [["date"]],
  NON_UTC_DATETIME_FIELDS: [],
};

export const REFERENCE_FIELDS = ["location"];

export const LINE_ACTIONS = [
  ["quickView", "pdf", "email"],
  "template",
  "disassemble",
  "archive",
  "delete",
  "history",
  "sync",
];
export const BATCH_ACTIONS = [["pdf", "email"], "delete", "archive", "sync"];

export const OUTPUT_ITEM_CALCULATED_FIELDS = ["onSO"];
export const INPUT_ITEM_CALCULATED_FIELDS = ["locationBins"];

export const QUICK_VIEW_CONFIG = {
  tableConfig: [{ type: "inputs", label: i18n("global.Inputs") }],
  headerFields: [
    {
      name: "date",
      formatFunc: formatDateTimeToDate,
      label: i18n("columns.Date"),
    },
    {
      name: "number",
      label: i18n("frmLabel.ProcessNumber"),
      defaultValue: NO_REF_NUMBER_STRING,
    },
  ],
  fields: [
    {
      label: i18n("frmLabel.Location"),
      name: "location",
      formatFunc: extractName,
    },
  ],
  lines: [
    {
      name: "item",
      formatFunc: formatLinkableItem,
      heading: i18n("columns.Item"),
      align: "left",
      isItemField: true,
    },
    {
      name: "description",
      formatFunc: extractDescription,
      heading: i18n("columns.Description"),
      align: "left",
    },
    {
      name: "quantity",
      formatFunc: formatQuantityWithUom,
      footerFunc: getQuantityTotal,
      heading: i18n("columns.Quantity"),
      align: "right",
    },
    {
      name: "notes",
      heading: i18n("columns.Notes"),
      align: "left",
    },
  ],
};
